import { FeedResponseItem } from 'api/interface/FeedResponseItem.ts';
import { GenerateParamsData } from 'api/interface/GenerateParams.ts';
import { GeneratedMediaResponse, GenerateResponse } from 'api/interface/GenerateResponse.ts';
import { AssetDataIdentifier } from 'api/services/AssetsService';
import { samplersConfig } from 'core/config/samplersConfig';
import { CHARACTER_IN_PROMPT_CONSTANT, CHARACTER_REGEX_GLOBAL } from 'core/constants/constants';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { Qualities } from 'core/constants/enums/AvailableQualitiesEnum.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { Samplers } from 'core/constants/enums/SamplersEnum.ts';
import { getAspectRatioFromDimensions } from 'core/utils/utils';
import { MediaType } from '../constants/enums/MediaType.ts';
/* eslint-disable no-prototype-builtins */

/* eslint-disable class-methods-use-this */
export class MediaFormatter {
	getControlNetSettings = (
		controlNetData:
			| GenerateParamsData['controlNetOptions']
			| FeedResponseItem['params']['attributes']['control_net'],
	): Partial<GenerateParamsData['controlNetOptions']> => {
		if (controlNetData?.hasOwnProperty('inputImage')) {
			return controlNetData;
		}
		if (controlNetData?.hasOwnProperty('input_image')) {
			const controlNetOptions =
				controlNetData as FeedResponseItem['params']['attributes']['control_net'];
			return {
				inputImage: controlNetOptions.input_image,
				controlMode: controlNetOptions.control_mode,
				resizeMode: controlNetOptions.resize_mode,
				nonsdxl: controlNetOptions.nonsdxl,
				sdxl: controlNetOptions.sdxl,
			};
		}
		return undefined;
	};

	getFacelockSettings = (
		facelockData:
			| GenerateParamsData['facelockOptions']
			| FeedResponseItem['params']['attributes']['facelock'],
		mediaResponse: GeneratedMediaResponse,
	): GenerateParamsData['facelockOptions'] | undefined => {
		if (facelockData?.hasOwnProperty('inputImage')) {
			const { character } = mediaResponse;
			if (character && Object.keys(character).length > 0) return undefined;
			return facelockData as GenerateParamsData['facelockOptions'];
		}
		if (facelockData?.hasOwnProperty('input_image')) {
			const facelockOptions = facelockData as FeedResponseItem['params']['attributes']['facelock'];
			return {
				inputImage: facelockOptions.input_image,
			};
		}
		return undefined;
	};

	getFaceSwapSettings = (
		faceSwapData:
			| GenerateParamsData['faceSwapOptions']
			| FeedResponseItem['params']['attributes']['face_swap'],
		mediaResponse: GeneratedMediaResponse,
	) => {
		if (faceSwapData?.hasOwnProperty('faceSwapVideoData')) {
			const faceSwapOptions = faceSwapData as GenerateParamsData['faceSwapOptions'];
			return {
				preference: faceSwapOptions?.preference,
				metadata: faceSwapOptions?.faceSwapVideoData?.[AssetDataIdentifier.META],
				sourceImage: faceSwapOptions?.faceSwapImageData?.[AssetDataIdentifier.URL],
				targetVideo: faceSwapOptions?.faceSwapVideoData?.[AssetDataIdentifier.URL],
			};
		}
		if (faceSwapData?.hasOwnProperty('target_video')) {
			const faceSwapOptions = faceSwapData as FeedResponseItem['params']['attributes']['face_swap'];
			const { duration, frames, size } = mediaResponse?.metadata ?? {};
			return {
				sourceImage: faceSwapOptions.src_image,
				targetVideo: faceSwapOptions.target_video,
				preference: faceSwapOptions.preference,
				metadata: {
					duration,
					frames,
					size,
				},
			};
		}
		return undefined;
	};

	getLipSyncSettings = (
		lipSyncData:
			| GenerateParamsData['lipSyncOptions']
			| FeedResponseItem['params']['attributes']['lip_sync'],
		mediaResponse: GeneratedMediaResponse,
	) => {
		if (lipSyncData?.hasOwnProperty('lipSyncVideoData')) {
			const lipSyncOptions = lipSyncData as GenerateParamsData['lipSyncOptions'];
			return {
				metadata: lipSyncOptions?.lipSyncVideoData?.[AssetDataIdentifier.META],
				targetVideo: lipSyncOptions?.lipSyncVideoData?.[AssetDataIdentifier.URL],
				sourceImage: lipSyncOptions?.lipSyncImageData?.[AssetDataIdentifier.URL],
				script: lipSyncOptions?.lipSyncScript,
				voiceId: lipSyncOptions?.lipSyncVoiceData?.id,
				voiceThumbnail: lipSyncOptions?.lipSyncVoiceData?.thumbnail,
				sourceAudio: lipSyncOptions?.lipSyncAudioData?.[AssetDataIdentifier.URL],
			};
		}
		if (lipSyncData?.hasOwnProperty('target_video')) {
			const lipSyncOptions = lipSyncData as FeedResponseItem['params']['attributes']['lip_sync'];
			const { duration, frames, size } = mediaResponse?.metadata ?? {};
			return {
				script: lipSyncOptions.script,
				sourceImage: lipSyncOptions.src_image,
				targetVideo: lipSyncOptions.target_video,
				voiceId: lipSyncOptions.voice_id,
				sourceAudio: lipSyncOptions.src_audio,
				metadata: {
					duration,
					frames,
					size,
				},
			};
		}
		return undefined;
	};

	getVideoAnyoneSettings = (
		videoAnyoneData:
			| GenerateParamsData['videoAnyoneOptions']
			| FeedResponseItem['params']['attributes']['video_anyone'],
		mediaResponse: GeneratedMediaResponse,
	) => {
		if (videoAnyoneData?.hasOwnProperty('videoAnyone')) {
			const videoAnyoneOptions = videoAnyoneData as GenerateParamsData['videoAnyoneOptions'];
			return {
				prompt: videoAnyoneOptions?.videoAnyone?.prompt,
				sourceImage: videoAnyoneOptions?.videoAnyone?.imageUrl,
			};
		}
		if (videoAnyoneData?.hasOwnProperty('image_url')) {
			const videoAnyoneOptions =
				videoAnyoneData as FeedResponseItem['params']['attributes']['video_anyone'];
			const { size, duration, frames } = mediaResponse?.metadata ?? {};
			return {
				prompt: videoAnyoneOptions?.prompt,
				sourceImage: videoAnyoneOptions?.image_url,
				metadata: {
					duration,
					frames,
					size,
				},
			};
		}
		return undefined;
	};

	getTextToVideoSettings = (
		textToVideoData:
			| GenerateParamsData['textToVideoOption']
			| FeedResponseItem['params']['attributes']['txt_to_vid'],
		mediaResponse: GeneratedMediaResponse,
	) => {
		if (textToVideoData?.hasOwnProperty('duration')) {
			const textToVideoOptions =
				textToVideoData as FeedResponseItem['params']['attributes']['txt_to_vid'];
			const { size, duration, frames } = mediaResponse?.metadata ?? {};

			const { character } = mediaResponse;
			const usernameRegex = CHARACTER_REGEX_GLOBAL;
			let prompt = textToVideoOptions?.prompt;
			if (usernameRegex.test(prompt ?? '')) {
				prompt = prompt.includes(CHARACTER_IN_PROMPT_CONSTANT)
					? (prompt ?? '').replace(usernameRegex, `^${character?.name}^`)
					: prompt;
			}
			return {
				prompt,
				metadata: {
					duration,
					frames,
					size,
				},
			};
		}
		return undefined;
	};

	getChangeItSettings = (
		changeItData:
			| GenerateParamsData['changeItOptions']
			| FeedResponseItem['params']['attributes']['segment'],
		changeItParams?: FeedResponseItem['params']['segment'],
	) => {
		if (changeItData?.hasOwnProperty('inputImage')) {
			return changeItData;
		}
		if (changeItData?.hasOwnProperty('input_image')) {
			const changeItOptions = changeItData as FeedResponseItem['params']['attributes']['segment'];
			return {
				inputImage: changeItOptions.input_image,
				whatToChange: changeItParams?.find,
				changeTo: changeItParams?.replace,
			};
		}
		return undefined;
	};

	getMediaCheckpointSettings = (
		mediaResponse?: GeneratedMediaResponse,
		settings?: Pick<
			GenerateParamsData,
			| 'AIGuidanceInput'
			| 'samplerInput'
			| 'quality'
			| 'samplingStepsInput'
			| 'selectedLorasMap'
			| 'batchSizeInput'
		>,
	): {
		model?: string;
		style?: string;
		steps?: number;
		guidance?: number;
		quality?: Qualities;
		sampler?: Samplers;
	} => {
		const { model, style } = mediaResponse;
		if (model?.settings != null || style?.settings != null) {
			let checkpointData = null;
			if (model) checkpointData = model;
			if (style) checkpointData = style;
			if (checkpointData) {
				return {
					...(model && { model: model.name }),
					...(style && { style: style.id }),
					steps: checkpointData?.settings?.steps,
					guidance: checkpointData?.settings?.cfg_scale,
					quality: checkpointData?.settings?.quality,
					sampler: Object.values(Samplers).find(
						(sKey) => samplersConfig[sKey]?.name === checkpointData?.settings?.sampler,
					),
				};
			}
			return {};
		}
		if (model?.settings == null && style?.settings == null) {
			return {
				...(model && { model: model.name }),
				...(style && { style: style.id }),
				steps: settings?.samplingStepsInput,
				guidance: settings?.AIGuidanceInput,
				quality: settings?.quality,
				sampler: settings?.samplerInput,
			};
		}
		return {};
	};

	getMediaDimensions = (mediaResponse: GeneratedMediaResponse) => {
		const { height, width } = mediaResponse.dim ?? mediaResponse?.metadata?.dim ?? {};
		const aspectRatio = height !== 0 ? width / height : 1;
		const aspectRatioType =
			height !== 0 ? getAspectRatioFromDimensions({ width, height }) : AspectRatio.SQUARE;
		return {
			height,
			width,
			aspectRatio,
			aspectRatioType,
		};
	};

	getMediaPlacementDetails = (mediaResponse: GeneratedMediaResponse) => {
		const mediaDimensions = this.getMediaDimensions(mediaResponse);
		return {
			...mediaDimensions,
		};
	};

	getMiscGenerationDetails = (mediaResponse: GeneratedMediaResponse) => {
		const { status, seed } = mediaResponse;
		return {
			loading: status === 'pending' || status === 'initiated',
			status,
			seed: status === 'success' ? seed : null,
		};
	};

	getCharacterDetails = (
		mediaResponse: GeneratedMediaResponse,
		generateParams: GenerateParamsData = null,
	) => {
		const { character } = mediaResponse;
		if (character) return character;
		if (generateParams?.character) return generateParams?.character;
		return undefined;
	};

	getPromptDetails = (
		response: GenerateResponse | FeedResponseItem,
		mediaResponse: GeneratedMediaResponse,
		generateParamsData?: GenerateParamsData,
	) => {
		if (response.hasOwnProperty('params')) {
			const { prompt: userPrompt, negative_prompt: negativePrompt } = (response as FeedResponseItem)
				.params;
			const { character } = mediaResponse;
			const usernameRegex = CHARACTER_REGEX_GLOBAL;
			let prompt = userPrompt;
			if (usernameRegex.test(userPrompt)) {
				prompt = userPrompt.replace(usernameRegex, `^${character?.name}^`);
			}
			return {
				prompt,
				negativePrompt,
			};
		}
		return {
			prompt: generateParamsData?.prompt,
			negativePrompt: generateParamsData?.negativePrompt,
		};
	};

	getGenerationState = (
		response: GenerateResponse | FeedResponseItem,
		generateParamsData?: GenerateParamsData,
	) => {
		if (response.hasOwnProperty('params')) {
			const { attributes } = (response as FeedResponseItem).params;
			if (attributes) {
				return {
					generationType: attributes.generation_type ?? '',
					musicVideoStates: attributes.state ?? '',
				};
			}
		}
		return {
			generationType: generateParamsData.generationType ?? '',
			musicVideoStates: generateParamsData.state ?? '',
		};
	};

	getImageToImageSettings = (
		response: GenerateResponse | FeedResponseItem,
		mediaResponse: GeneratedMediaResponse,
	): {
		isTrueTouch: boolean;
		isChangeIt: boolean;
		isUpscale: boolean;
		isFaceReplace: boolean;
		isImageSettingsHidden: boolean;
		isPromptHidden: boolean;
		isSingleMediaGeneration: boolean;
		isClothSwap: boolean;
	} => {
		let isTrueTouch: boolean;
		let isChangeIt: boolean;
		let isUpscale: boolean;
		let isFaceReplace: boolean;
		let isClothSwap: boolean;
		const isVideo = mediaResponse.media_type === MediaType.VIDEO;
		let generationType: string;

		if (response.hasOwnProperty('is_true_touch')) {
			isTrueTouch = (response as FeedResponseItem).is_true_touch;
			isChangeIt = (response as FeedResponseItem).is_segment;
			isUpscale = (response as FeedResponseItem).is_upscale;
			isFaceReplace = (response as FeedResponseItem).is_face_replace;
			isClothSwap = (response as FeedResponseItem).is_cloth_swap;
			generationType = (response as FeedResponseItem).params.attributes.generation_type;
		} else {
			isTrueTouch = mediaResponse.is_true_touch;
			isChangeIt = mediaResponse.is_segment;
			isUpscale = mediaResponse.is_upscale;
			isFaceReplace = mediaResponse.is_face_replace;
			isClothSwap = mediaResponse.is_cloth_swap;
			generationType = mediaResponse.generation_type;
		}

		return {
			isTrueTouch,
			isChangeIt,
			isUpscale,
			isFaceReplace,
			isClothSwap,
			isImageSettingsHidden:
				isTrueTouch ||
				isChangeIt ||
				isUpscale ||
				isFaceReplace ||
				isClothSwap ||
				generationType === GenerationType.OBJECT_PLACEMENT,
			isPromptHidden:
				isTrueTouch ||
				isChangeIt ||
				isUpscale ||
				isVideo ||
				isFaceReplace ||
				isClothSwap ||
				generationType === GenerationType.OBJECT_PLACEMENT,
			isSingleMediaGeneration:
				isTrueTouch ||
				isChangeIt ||
				isUpscale ||
				isVideo ||
				isFaceReplace ||
				isClothSwap ||
				generationType === GenerationType.OBJECT_PLACEMENT,
		};
	};

	getImageReferenceStyleSettings = (
		imageReferenceData:
			| GenerateParamsData['imageReferenceOptions']['fluxImageToImage']
			| FeedResponseItem['params']['attributes']['flux_image_to_image'],
		mediaResponse: GeneratedMediaResponse,
	): { referenceImage: string } | undefined => {
		if (imageReferenceData?.hasOwnProperty('referenceImage')) {
			return {
				referenceImage: mediaResponse.style?.preview_image,
			};
		}
		return {
			referenceImage: (
				imageReferenceData as FeedResponseItem['params']['attributes']['flux_image_to_image']
			)?.reference_image?.input_image,
		};
	};
}

/* eslint-enable no-prototype-builtins */
/* eslint-enable class-methods-use-this */
